import React from "react";
import { Box, RadioGroup, Typography } from "@mui/material";
import { DotLegalHeader, DotLegalRadioButton, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import DotLegalWarningTriangle from "@common/components/dotLegalWarningTriangle/DotLegalWarningTriangle.tsx";
import { useTranslation } from "@localization/useTranslation";
import { PeriodUnit } from "../../EntityTemplate.types.ts";
import { CustomDatePeriodFieldProps, useCustomDatePeriodField } from "./CustomDatePeriodField.hooks.ts";
import { hintSx } from "./calculatedField.styles.ts";

function CustomDatePeriodField(props: CustomDatePeriodFieldProps) {
    const { translateString } = useTranslation();
    const {
        fromOptionId,
        toOptionId,
        unit,
        dateOptions,
        example,
        onFromChange,
        onToChange,
        onUnitChange,
        valueText,
        unitText,
        isEndBeforeStart,
        missingDateName,
        datesDescription,
    } = useCustomDatePeriodField(props);

    if (props.isPreview) {
        return (
            <Box>
                <DotLegalHeader headerStyle="extraSmall" marginBottom={1}>
                    {props.field.customInputFieldName!}
                </DotLegalHeader>
                <DotLegalSelect
                    label={translateString("fromDate")}
                    options={dateOptions}
                    selectedItem={fromOptionId}
                    onChange={onFromChange}
                    toolTipText={props.fieldDescription}
                    noOptionsLabel={translateString("noOptions")}
                    disableClearable
                />
                <DotLegalSelect
                    label={translateString("toDate")}
                    options={dateOptions}
                    selectedItem={toOptionId}
                    onChange={onToChange}
                    noOptionsLabel={translateString("noOptions")}
                    disableClearable
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={hintSx}>{translateString("unit")}</Typography>
                    <RadioGroup row value={unit} onChange={(event) => onUnitChange(Number(event.target.value) as PeriodUnit)}>
                        <DotLegalRadioButton value={PeriodUnit.Years} labelPlacement="end" label={translateString("years")} />
                        <DotLegalRadioButton value={PeriodUnit.Months} labelPlacement="end" label={translateString("months")} />
                    </RadioGroup>
                </Box>
                <Typography sx={hintSx}>{translateString("datePeriodInfo")}</Typography>
                <Typography sx={hintSx}>{translateString("datePeriodExample", { example })}</Typography>
            </Box>
        );
    }

    return (
        <DotLegalTextField
            label={props.field.customInputFieldName!}
            toolTipText={props.fieldDescription}
            value={valueText}
            debounce={false}
            isLoading={props.isLoading}
            errorText={props.errorText}
            helperText={datesDescription}
            endAdorment={getEndAdornment()}
            disabled
        />
    );

    // Same place as the calculated field's currency conflict warning, so both computed fields warn the same way
    function getEndAdornment() {
        if (isEndBeforeStart) {
            return <DotLegalWarningTriangle warningText={translateString("datePeriodEndBeforeStartWarning")} />;
        }

        if (missingDateName) {
            return <DotLegalWarningTriangle warningText={translateString("datePeriodMissingDateWarning", { name: missingDateName })} />;
        }

        return valueText ? <Box sx={(theme) => ({ color: theme.palette.grey[400], fontSize: 12 })}>{unitText}</Box> : undefined;
    }
}

export default CustomDatePeriodField;
